import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CharacterDetail } from "../services/schemas/gw2Schemas";
import type {
  BankTab,
  MaterialEntry,
  SharedInventoryItem,
} from "../services/accountDomain";

type Ruleset = {
  id: string;
  [key: string]: unknown;
};

type StoreState = {
  apiKey: string | null;
  setApiKey: (key: string | null) => void;

  language: string;
  setLanguage: (lang: string) => void;

  accountData: unknown;
  setAccountData: (data: unknown) => void;

  bankItems: BankTab[];
  materialStorage: MaterialEntry[];
  sharedInventory: SharedInventoryItem[];
  characters: CharacterDetail[];
  wallet: unknown[];
  selectedCharacter: string | null;

  setBankItems: (items: BankTab[]) => void;
  setMaterialStorage: (materials: MaterialEntry[]) => void;
  setSharedInventory: (inventory: SharedInventoryItem[]) => void;
  setCharacters: (chars: CharacterDetail[]) => void;
  setWallet: (wallet: unknown[]) => void;
  setSelectedCharacter: (char: string | null) => void;

  itemCache: Record<number, unknown>;
  addToItemCache: (items: Record<number, unknown>) => void;
  clearItemCache: () => void;

  priceCache: Record<number, unknown>;
  addToPriceCache: (prices: Record<number, unknown>) => void;

  activeTab: string;
  setActiveTab: (tab: string) => void;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  rarityFilter: string[];
  setRarityFilter: (rarities: string[]) => void;

  typeFilter: string[];
  setTypeFilter: (types: string[]) => void;

  sortBy: string;
  setSortBy: (sort: string) => void;

  showPrices: boolean;
  setShowPrices: (show: boolean) => void;

  tpPriceType: string;
  setTpPriceType: (type: string) => void;

  customRulesets: Ruleset[];
  activeRulesetId: string | null;
  setCustomRulesets: (rulesets: Ruleset[]) => void;
  addCustomRuleset: (ruleset: Ruleset) => void;
  updateCustomRuleset: (id: string, ruleset: Ruleset) => void;
  deleteCustomRuleset: (id: string) => void;
  setActiveRuleset: (id: string | null) => void;
  resetToDefaultRules: () => void;

  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  clearAllData: () => void;
};

export const useStore = create<StoreState>()(
  persist(
    (set) => ({
      apiKey: null,
      setApiKey: (key) => {
        set({ apiKey: key });
      },

      language: "en",
      setLanguage: (lang) => {
        set({ language: lang });
      },

      accountData: null,
      setAccountData: (data) => set({ accountData: data }),

      bankItems: [],
      materialStorage: [],
      sharedInventory: [],
      characters: [],
      wallet: [],
      selectedCharacter: null,

      setBankItems: (items) => set({ bankItems: items }),
      setMaterialStorage: (materials) => set({ materialStorage: materials }),
      setSharedInventory: (inventory) => set({ sharedInventory: inventory }),
      setCharacters: (chars) => set({ characters: chars }),
      setWallet: (wallet) => set({ wallet }),
      setSelectedCharacter: (char) => set({ selectedCharacter: char }),

      itemCache: {},
      addToItemCache: (items) =>
        set((state) => ({
          itemCache: { ...state.itemCache, ...items },
        })),
      clearItemCache: () => set({ itemCache: {} }),

      priceCache: {},
      addToPriceCache: (prices) =>
        set((state) => ({
          priceCache: { ...state.priceCache, ...prices },
        })),

      activeTab: "bank",
      setActiveTab: (tab) => set({ activeTab: tab }),

      searchQuery: "",
      setSearchQuery: (query) => set({ searchQuery: query }),

      rarityFilter: [],
      setRarityFilter: (rarities) => set({ rarityFilter: rarities }),

      typeFilter: [],
      setTypeFilter: (types) => set({ typeFilter: types }),

      sortBy: "name",
      setSortBy: (sort) => set({ sortBy: sort }),

      showPrices: true,
      setShowPrices: (show) => set({ showPrices: show }),

      tpPriceType: "buy",
      setTpPriceType: (type) => set({ tpPriceType: type }),

      customRulesets: [],
      activeRulesetId: null,
      setCustomRulesets: (rulesets) => set({ customRulesets: rulesets }),
      addCustomRuleset: (ruleset) =>
        set((state) => ({
          customRulesets: [...state.customRulesets, ruleset],
        })),
      updateCustomRuleset: (id, ruleset) =>
        set((state) => ({
          customRulesets: state.customRulesets.map((entry) =>
            entry.id === id ? ruleset : entry,
          ),
        })),
      deleteCustomRuleset: (id) =>
        set((state) => ({
          customRulesets: state.customRulesets.filter(
            (entry) => entry.id !== id,
          ),
          activeRulesetId:
            state.activeRulesetId === id ? null : state.activeRulesetId,
        })),
      setActiveRuleset: (id) => set({ activeRulesetId: id }),
      resetToDefaultRules: () => set({ activeRulesetId: null }),

      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),

      clearAllData: () =>
        set({
          apiKey: null,
          accountData: null,
          bankItems: [],
          materialStorage: [],
          sharedInventory: [],
          characters: [],
          wallet: [],
          selectedCharacter: null,
        }),
    }),
    {
      name: "gw2-meta-event-tracker-storage",
      partialize: (state) => ({
        apiKey: state.apiKey,
        language: state.language,
        showPrices: state.showPrices,
        tpPriceType: state.tpPriceType,
        sortBy: state.sortBy,
        customRulesets: state.customRulesets,
        activeRulesetId: state.activeRulesetId,
      }),
    },
  ),
);
